<template>
    <div class="w-full">
        <div v-if="isProcessing" class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex items-center gap-2 text-sm text-slate-500">
                <UIcon name="i-heroicons-arrow-path" class="h-4 w-4 animate-spin text-blue-500" />
                <span>Building followup arc...</span>
            </div>
        </div>

        <div v-else-if="hasError" class="rounded-xl border border-red-200 bg-red-50 p-4">
            <div class="flex items-center gap-2 text-sm text-red-700">
                <UIcon name="i-heroicons-x-circle" class="h-4 w-4" />
                <span>{{ part.errorText || 'Failed to build followup arc' }}</span>
            </div>
        </div>

        <div v-else-if="toolData" class="rounded-2xl border border-indigo-200 bg-indigo-50/70 p-3 shadow-sm">
            <div class="mb-3 flex items-start justify-between gap-3 px-1">
                <div class="min-w-0">
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                        <UIcon name="i-heroicons-arrow-trending-up" class="h-4 w-4" />
                        Followup arc
                    </div>
                    <p class="mt-1 text-sm leading-5 text-slate-700">{{ compactSummary }}</p>
                    <p class="mt-1 text-xs text-slate-500">Bet: {{ toolData.bet.label }}</p>
                </div>
                <button
                    type="button"
                    class="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-50"
                    @click="emit('draft-edits', toolData)"
                >
                    Draft edits
                    <UIcon name="i-heroicons-pencil-square" class="h-3.5 w-3.5" />
                </button>
            </div>

            <div class="space-y-3">
                <div
                    v-for="(step, index) in toolData.steps"
                    :key="`${toolData.bet.id}-followup-${index}`"
                    class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                    <div class="flex flex-wrap items-center gap-2">
                        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
                            {{ step.stepLabel }}
                        </span>
                        <span v-if="step.timing" class="text-xs text-slate-500">{{ step.timing }}</span>
                    </div>

                    <dl class="mt-3 grid gap-2 text-sm leading-5 text-slate-700">
                        <div>
                            <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Goal</dt>
                            <dd>{{ step.goal }}</dd>
                        </div>
                        <div>
                            <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Angle</dt>
                            <dd>{{ step.angle }}</dd>
                        </div>
                        <div>
                            <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">CTA</dt>
                            <dd>{{ step.cta }}</dd>
                        </div>
                    </dl>

                    <div v-if="step.snippet" class="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Snippet</div>
                        <p class="whitespace-pre-wrap text-xs leading-5 text-slate-700">{{ step.snippet }}</p>
                    </div>

                    <p v-if="step.avoidRepeating" class="mt-2 text-xs leading-4 text-slate-500">
                        Avoid repeating: {{ step.avoidRepeating }}
                    </p>
                </div>
            </div>

            <div v-if="toolData.strategyNotes?.length" class="mt-3 rounded-xl border border-indigo-100 bg-white px-3 py-2">
                <div class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Strategy notes</div>
                <ul class="space-y-1 text-xs leading-4 text-slate-600">
                    <li v-for="note in toolData.strategyNotes" :key="note">{{ note }}</li>
                </ul>
            </div>
        </div>

        <div v-else class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            No followup arc available.
        </div>
    </div>
</template>

<script setup lang="ts">
interface CreativeBetReference {
    id: string;
    label: string;
    theme?: 'proof' | 'research' | 'pain' | 'angle' | 'channel' | 'custom';
    preview: string;
    rationale?: string;
}

interface FollowupArcStep {
    stepLabel: string;
    timing?: string;
    goal: string;
    angle: string;
    cta: string;
    snippet?: string;
    avoidRepeating?: string;
}

interface CreativeBetFollowupArcData {
    campaignId: number;
    summary: string;
    bet: CreativeBetReference;
    steps: FollowupArcStep[];
    strategyNotes?: string[];
}

const props = defineProps<{
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

const emit = defineEmits<{
    'draft-edits': [data: CreativeBetFollowupArcData];
}>();

const isProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return true;
});

const hasError = computed(() => props.part.errorText !== undefined && props.part.errorText !== '');

const toolData = computed(() => {
    const raw = (props.part.output ?? props.part.input) as Partial<CreativeBetFollowupArcData> | undefined;
    if (!raw || !isCreativeBetReference(raw.bet) || !Array.isArray(raw.steps)) return null;

    const steps = raw.steps.filter(isFollowupArcStep).slice(0, 3);
    if (steps.length === 0) return null;

    return {
        campaignId: Number(raw.campaignId),
        summary: raw.summary || 'Use this arc to extend the selected bet beyond the first touch.',
        bet: raw.bet,
        steps,
        strategyNotes: Array.isArray(raw.strategyNotes)
            ? raw.strategyNotes.filter((note): note is string => typeof note === 'string').slice(0, 3)
            : undefined,
    } satisfies CreativeBetFollowupArcData;
});

const compactSummary = computed(() => normalizeInlineWhitespace(toolData.value?.summary ?? ''));

function normalizeInlineWhitespace(text: string): string {
    return text
        .replace(/\\n/g, '\n')
        .replace(/\r\n/g, '\n')
        .trim()
        .split('\n')
        .map(line => line.trim().replace(/[ \t]+/g, ' '))
        .join('\n');
}

function isCreativeBetReference(value: unknown): value is CreativeBetReference {
    if (!value || typeof value !== 'object') return false;
    const bet = value as Partial<CreativeBetReference>;
    return typeof bet.id === 'string'
        && typeof bet.label === 'string'
        && typeof bet.preview === 'string';
}

function isFollowupArcStep(value: unknown): value is FollowupArcStep {
    if (!value || typeof value !== 'object') return false;
    const step = value as Partial<FollowupArcStep>;
    return typeof step.stepLabel === 'string'
        && typeof step.goal === 'string'
        && typeof step.angle === 'string'
        && typeof step.cta === 'string';
}
</script>
